import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  Loading,
  SelectInput,
  TextInput,
  required,
  useDataProvider,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Market, User } from '../dataProvider';

/**
 * Users edit — same pattern as StoreEdit (#26) but simpler: a user is ONE row in
 * ONE market, so there is no per-market fan-out — a single `update('users')`.
 *
 * Editable fields only (D-users-edit): name + tier. Everything else (email,
 * market, the DERIVED balance, signupMethod, createdAt) is read-only and NOT in
 * this form. Balance is never directly editable — it is derived from
 * transactions/cashouts; a future manual adjustment is a Phase-3 transaction,
 * not an edit here.
 *
 * AUDIT FOR FREE: no audit code here. Saving calls `dataProvider.update('users')`
 * and the audit wrapper (#25) records the diff (e.g. `tier: free → plus`) on the
 * users row automatically.
 *
 * GUARDS: the route is gated `users:write` (a viewer is denied on a direct
 * /users/:id/edit hit, not just missing the button); and the same record-level
 * region scoping as the detail (decision 6) blocks editing a user outside the
 * viewer's market.
 */

const tierChoices = [
  { id: 'free', name: 'Free' },
  { id: 'plus', name: 'Plus' },
];

interface UserFormValues {
  name: string;
  tier: string;
}

const UserEditForm = ({ record }: { record: User }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const defaultValues: UserFormValues = {
    name: record.name,
    tier: record.tier,
  };

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as UserFormValues;
    const data = { name: values.name, tier: values.tier };
    const previousData = { name: record.name, tier: record.tier };

    try {
      if (JSON.stringify(data) !== JSON.stringify(previousData)) {
        await dataProvider.update('users', {
          id: record.id,
          data,
          previousData,
        });
      }
      notify('User updated', { type: 'success' });
      redirect('show', 'users', record.id);
    } catch {
      notify('Could not save the user', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Edit {record.name}
      </Typography>
      <Form defaultValues={defaultValues} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="name"
              label="Name"
              validate={required('Name is required')}
              fullWidth
            />
            <SelectInput
              source="tier"
              label="Tier"
              choices={tierChoices}
              validate={required()}
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Save
          </Button>
          <Button
            type="button"
            onClick={() => redirect('show', 'users', record.id)}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** The edit view. Exported for testing without the access wrapper. */
export const UserEditView = () => {
  const { id } = useParams();
  const { accessibleRegions } = useRegion();
  const { data: record, isLoading } = useGetOne<User>('users', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;

  // Record-level region guard (decision 6) — cannot edit a user out of scope.
  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  return <UserEditForm record={record} />;
};

/**
 * Gated entry point registered as the `users` Resource's edit. GUARDED
 * `users:write` — a viewer hitting /users/:id/edit directly is denied.
 */
export const UserEdit = () => (
  <RequireAccess capability="users:write">
    <UserEditView />
  </RequireAccess>
);
